// Cross-environment clipboard write.
//
// Three failure modes we've hit in production:
// - The Claude Code preview iframe blocks `navigator.clipboard.writeText`.
// - iOS WKWebView (Capacitor) sometimes rejects `navigator.clipboard` when
//   user-activation has crossed an `await`, or returns success while
//   silently writing nothing.
// - The standard "hidden textarea + execCommand('copy')" desktop fallback
//   doesn't work on iOS because `readonly` + `ta.select()` won't select
//   inside a not-yet-focused hidden element.
//
// Strategy: try the modern API first, then fall back to a Range-API
// selection on a writable, visible-but-offscreen textarea. Returns true
// on success.
export async function copyToClipboard(text: string): Promise<boolean> {
	if (!text) return false;
	try {
		if (navigator?.clipboard?.writeText) {
			await navigator.clipboard.writeText(text);
			return true;
		}
	} catch {
		// fall through
	}
	try {
		const ta = document.createElement('textarea');
		ta.value = text;
		ta.contentEditable = 'true';
		// Off-screen but selectable. Don't use opacity:0 alone — iOS won't
		// let you select inside an element with no visible footprint.
		ta.style.position = 'fixed';
		ta.style.top = '0';
		ta.style.left = '0';
		ta.style.width = '1px';
		ta.style.height = '1px';
		ta.style.padding = '0';
		ta.style.border = 'none';
		ta.style.outline = 'none';
		ta.style.boxShadow = 'none';
		ta.style.background = 'transparent';
		ta.style.fontSize = '16px'; // prevent iOS zoom which breaks selection
		document.body.appendChild(ta);
		ta.focus();
		// iOS: explicit Range + setSelectionRange is more reliable than .select().
		const range = document.createRange();
		range.selectNodeContents(ta);
		const sel = window.getSelection();
		if (sel) {
			sel.removeAllRanges();
			sel.addRange(range);
		}
		ta.setSelectionRange(0, text.length);
		const ok = document.execCommand('copy');
		document.body.removeChild(ta);
		return ok;
	} catch (e) {
		console.warn('[copyToClipboard] fallback failed:', e);
		return false;
	}
}
